<template>
    <li
        v-qa="label.replace(/\s\(.+\)/, '')"
        :class="classFilterItem"
        class="filter-item"
    >
        <div
            v-if="removable"
            class="remove"
        ></div>

        <ThLink
            :aria-label="labelAria"
            :disabled="disabled || totalResults === 0"
            :to="selected || removable ? removeLink : addLink"
            class="link"
        >
            <div
                v-if="!noIcons"
                class="icon"
            >
                <slot></slot>
            </div>

            <div class="label">{{ label }} ({{ totalResults }})</div>
        </ThLink>
    </li>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ButtonRouteRelative } from '@/components/ui/buttons/useThButton'

import ThLink from '@/components/ui/buttons/ThLink.vue'

const props = defineProps<{
    label: string
    selected?: boolean
    noIcons?: boolean
    addLink?: ButtonRouteRelative
    removeLink?: ButtonRouteRelative
    totalResults: number
    disabled?: boolean
}>()

const removable = computed(() => Boolean(props.removeLink) && !props.addLink)

const classFilterItem = computed(() => ({
    'selected': props.selected && !removable.value,
    'no-icons': props.noIcons,
    'disabled': props.disabled,
}))

const { t } = useI18n()

const labelAria = computed(() =>
    props.selected || removable.value
        ? t('catalogue.filters.rem', { f: props.label })
        : props.label
)
</script>

<style lang="scss" scoped>
.filter-item {
    position: relative;
    width: 100%;
    min-height: 86px;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;

    font-family: $effra-font-stack;
    font-weight: 400;
    font-size: 0.675rem;
    line-height: 1em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    text-align: center;
    color: $slate;

    &.disabled {
        cursor: not-allowed;
    }

    :deep(.disabled) {
        opacity: 0.2;

        .icon {
            background: $grey-200;
        }
    }

    .link {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    &.no-icons {
        width: initial;
        min-height: initial;
        text-align: left;

        .link {
            flex-direction: row;
            align-items: unset;
        }
    }

    &.selected {
        .label {
            font-weight: 500;
            color: $slate;
        }

        .icon {
            border: 1px solid $grey-500;
        }
    }

    .remove {
        position: absolute;
        width: 18px;
        height: 18px;
        text-align: center;
        line-height: 18px;
        background: $blue;
        border-radius: 50%;
        color: $white;
        top: 2px;
        right: 6px;
        font-weight: 600;
        font-size: 0;
        pointer-events: none;

        &::before {
            font-family: $icon-font;
            content: $icon-close;
            font-size: 8px;
            line-height: 19px;
            text-align: center;
            color: $white;
            display: block;
        }
    }

    .icon {
        margin: 0 0 5px;
        width: 100%;
        aspect-ratio: 1/1;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid $grey-300;
        border-radius: 8px;
    }
}
</style>
